import React, { useState, useEffect, useRef } from "react";
import {
  FormControl,
  FormLabel,
  Typography,
  Box,
  InputProps,
} from "@mui/material";
import {
  Control,
  Controller,
  ControllerProps,
  useWatch,
} from "react-hook-form";
import { Currencies, Locales, useCurrencyFormat } from "input-currency-react";
import { StyledInput } from "@/components/styled";
import { ReactNode } from "react";
import TooltipInfoIcon from "../TooltipInfoIcon";
import { formatFinanceAmount } from "@/utils/financeUtils";
import { usePreventNumberInputScroll } from "@/hooks/usePreventNumberInputScroll";

const CustomCurrencyInput = ({ value, ...props }: any) => {
  const inputRef = useRef<HTMLInputElement>(null);
  usePreventNumberInputScroll(inputRef);

  const [formattedValue, handleOnChange, handleOnKeyDown] = useCurrencyFormat(
    value,
    {
      ...props?.options,
      onChangeCallBack: props?.onChangeEvent,
    }
  );

  const displayValue = formattedValue || value || "";

  return (
    <StyledInput
      onChange={handleOnChange}
      onKeyDown={handleOnKeyDown}
      value={displayValue}
      fullWidth
      {...props}
      inputRef={inputRef}
    />
  );
};

type FormInputProps = InputProps & {
  name: string;
  control: Control<any, any>;
  label?: string;
  helperText?: ReactNode;
  controlProps?: Partial<ControllerProps>;
  defaultValue?: string;
  maxValue?: number;
  labelTooltip?: string;
};

const FormCurrencyInput = ({
  name,
  control,
  label,
  helperText,
  controlProps,
  defaultValue = "",
  maxValue,
  labelTooltip,
  value,
  ...props
}: FormInputProps & { value?: string }) => {
  const initialValue =
    name === "externalIncentives" &&
    (defaultValue === "$0.00" || defaultValue === "0.00")
      ? "$0.00"
      : defaultValue;

  const displayValue = value || defaultValue;
  const [formattedValue, setFormattedValue] = useState(displayValue);
  const isRequired = !!controlProps?.rules?.required;
  useEffect(() => {
    if (value !== undefined) {
      setFormattedValue(value);
    }
  }, [value]);

  return (
    <FormControl
      component="fieldset"
      sx={{ width: "100%", paddingBottom: "24px" }}
    >
      <FormLabel component="legend">
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            color: (theme) => theme.palette.textGray1,
            paddingBottom: "2px",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          {label}
          {isRequired && <Typography sx={{ color: "red" }}>*</Typography>}
          {labelTooltip && (
            <Box sx={{ marginLeft: "4px" }}>
              <TooltipInfoIcon tooltipText={labelTooltip} />
            </Box>
          )}
        </Typography>
      </FormLabel>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange } }) => (
          <CustomCurrencyInput
            onChangeEvent={(_: any, maskedValue: any, value: any) => {
              if (
                value?.length <= 15 &&
                (!maxValue || value * 100 <= maxValue * 100)
              ) {
                setFormattedValue(maskedValue);
                onChange(value);
              }
            }}
            options={{
              // style: "decimal",
              allowNegative: false,
              locale: Locales["English (United States)"],
              i18nCurrency: Currencies["US Dollar"],
            }}
            value={formattedValue}
            {...props}
          />
        )}
      />
      {helperText}
    </FormControl>
  );
};

export default FormCurrencyInput;
